import { FC, useMemo, useEffect, useState, useRef, useLayoutEffect } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { Theme, ThemeTranslation } from '../core/_models'
import clsx from 'clsx'
import { useListView } from '../core/ListViewProvider'
import { DataLoading } from '../../../../../_dashboard/partials/components/loading/DataLoading'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { createOne, getAll, updateOne } from '../../../../services/common_requests'
import { useIntl } from 'react-intl'
import { useQueryRequest } from '../core/QueryRequestProvider'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { isNotEmpty } from '../../../../../_dashboard/helpers'
import { getBackofficeDefaultLanguage } from '../../../tours-management/configurations/models/personType'

type Props = {
  isThemeLoading: boolean
  Theme: Theme
}

/** Liste des codes depuis la réponse API (liste plate ou objet avec data). */
function extractLanguagesList(body: unknown): unknown[] {
  if (!body) return []
  if (Array.isArray(body)) return body
  if (typeof body === 'object' && body !== null) {
    const o = body as { data?: unknown }
    if (Array.isArray(o.data)) return o.data
    const nested = (o.data as { data?: unknown[] } | undefined)?.data
    if (Array.isArray(nested)) return nested
  }
  return []
}

/** Keep validation light: edited name checked in submit for the selected language (min 3). */
const editThemeSchema = Yup.object().shape({
  translations: Yup.array().of(
    Yup.object().shape({
      language: Yup.string(),
      name: Yup.string().max(120),
      _id: Yup.string(),
    }),
  ),
})

function normLang(l: unknown): string {
  return String(l ?? '')
    .trim()
    .toLowerCase()
}

function mergeThemeForForm(
  theme: Theme,
  langCodes?: string[],
): Theme & { translations: ThemeTranslation[] } {
  const raw = theme.translations?.length ? [...theme.translations] : []
  const fromApi = langCodes?.map(normLang).filter(Boolean) ?? []
  const existingKeys = Array.from(
    new Set(raw.map((t) => normLang(t.language)).filter(Boolean)),
  )
  const codes = Array.from(new Set(fromApi.length ? fromApi : existingKeys))
  const unified = codes.length ? codes : ['en']
  const byLang = new Map(raw.map((t) => [normLang(t.language), t]))
  return {
    ...theme,
    translations: unified.map((code) => {
      const prev = byLang.get(code)
      return {
        ...prev,
        language: code,
        name: prev?.name ?? '',
        _id: prev?._id,
      }
    }),
  }
}

/** Étend la liste des langues sans effacer ce que l’utilisateur a déjà saisi (évite resetForm au chargement de /languages). */
function mergeTranslationsPreserveInput(
  theme: Theme,
  langCodes: string[],
  previous: ThemeTranslation[],
): ThemeTranslation[] {
  const fresh = mergeThemeForForm(theme, langCodes).translations
  const map = new Map(previous.map((t) => [normLang(t.language), t]))
  return fresh.map((t) => {
    const old = map.get(normLang(t.language))
    if (old === undefined) return t
    return {
      ...t,
      name: old.name ?? t.name ?? '',
      _id: old._id ?? t._id,
    }
  })
}

const ThemeEditModalForm: FC<Props> = ({ Theme, isThemeLoading }) => {
  const intl = useIntl()
  const LANGUAGE_NAMES: { [key: string]: string } = useMemo(
    () => ({
      en: intl.formatMessage({ id: 'COMMON.ENGLISH_LANGUAGE' }),
      fr: intl.formatMessage({ id: 'COMMON.FRENSH_LANGUAGE' }),
      es: intl.formatMessage({ id: 'COMMON.ESPANGOL_LANGUAGE' }),
      de: intl.formatMessage({ id: 'COMMON.GERMAN_LANGUAGE' }),
      ar: intl.formatMessage({ id: 'COMMON.ARABIC_LANGUAGE' }),
    }),
    [intl],
  )

  const { itemIdForUpdate, setItemIdForUpdate } = useListView()
  const { state } = useQueryRequest()
  const [query] = useState<any>(state)
  const [languages, setLanguages] = useState<{ code: string; name: string }[]>([])
  const [selectedLanguage, setSelectedLanguage] = useState(() => normLang(getBackofficeDefaultLanguage()) || 'fr')

  const langCodes = useMemo(() => languages.map((l) => normLang(l.code)).filter(Boolean), [languages])

  const themeInitial = useMemo(() => mergeThemeForForm(Theme, langCodes), [Theme, langCodes])

  const serverThemeSig = useMemo(() => {
    const tr = Theme?.translations || []
    const idSig = tr
      .map((t) => `${String((t as ThemeTranslation)._id ?? 'x')}:${normLang(t.language)}`)
      .sort()
      .join(';')
    return `${String(Theme?._id ?? 'new')}:${isThemeLoading ? 1 : 0}:${idSig}`
  }, [Theme, isThemeLoading])

  useEffect(() => {
    void getAll(query, '/languages').then((data) => {
      const codes = extractLanguagesList(data).map(String).filter(Boolean)
      if (!codes.length) return
      const formattedLanguages = codes.map((code: string) => ({
        code: normLang(code) || code,
        name: LANGUAGE_NAMES[normLang(code) || code] || normLang(code).toUpperCase() || code.toUpperCase(),
      }))
      setLanguages(formattedLanguages)
    })
  }, [query, LANGUAGE_NAMES])

  /** Langue sélectionnée doit exister dans la liste renvoyée par l’API. */
  useEffect(() => {
    if (!languages.length) return
    setSelectedLanguage((cur) => {
      const codes = languages.map((l) => normLang(l.code))
      const normalized = normLang(cur)
      if (codes.includes(normalized)) return cur
      const def = normLang(getBackofficeDefaultLanguage())
      return codes.includes(def) ? def : codes[0]
    })
  }, [languages])

  const { refetchData } = useQueryResponse()

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetchData()
    }
    setItemIdForUpdate(undefined)
  }

  const translationsSnapshotRef = useRef<ThemeTranslation[]>([])
  const lastAppliedLangJoinRef = useRef<string>('')

  const formik = useFormik({
    initialValues: themeInitial,
    validationSchema: editThemeSchema,
    enableReinitialize: false,
    onSubmit: async (values, { setSubmitting }) => {
      const selectedTranslation = values.translations.find(
        (t) => normLang(t.language) === normLang(selectedLanguage),
      )
      const trimmed = selectedTranslation?.name?.trim() ?? ''

      setSubmitting(true)
      try {
        if (!trimmed || trimmed.length < 3) {
          toast.error(
            intl.formatMessage({
              id: 'ADMIN.COMMON.VALIDATION.THEME_NAME_MIN',
              defaultMessage: 'Theme name must be at least 3 characters.',
            }),
          )
          return
        }

        if (isNotEmpty(itemIdForUpdate)) {
          let response
          if (selectedTranslation?._id) {
            const payload = { name: trimmed }
            response = await updateOne(payload, `/themes/translation/${selectedTranslation._id}`)
          } else {
            const payload = {
              language: normLang(selectedLanguage) || 'en',
              name: trimmed,
            }
            response = await createOne(payload, `/themes/${itemIdForUpdate}/translations`)
          }
          if (response?.payload?.code === 2021) {
            toast.success(intl.formatMessage({ id: 'ADMIN.COMMON.SUCCESS.OPTION_UPDATED_SUCCESSFULLY' }), {
              position: 'top-right',
              autoClose: 4000,
            })
            cancel(true)
          } else if (response?.payload?.code === 4401) {
            toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.ID_NOT_EXIST' }), {
              position: 'top-right',
              autoClose: 4000,
            })
          } else if (response?.payload?.code === 9100) {
            toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.ID_NOT_FOUND' }), {
              position: 'top-right',
              autoClose: 4000,
            })
          } else {
            toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_OPTION' }), {
              position: 'top-right',
              autoClose: 4000,
            })
          }
        } else {
          const payload = {
            name: trimmed,
            translations: [
              {
                language: normLang(selectedLanguage) || 'en',
                name: trimmed,
              },
            ],
          }
          const response = await createOne(payload, '/themes')
          if (response?.payload?.code === 2021) {
            toast.success(intl.formatMessage({ id: 'ADMIN.COMMON.SUCCESS.THEMES_ADDED_SUCCESSFULLY' }), {
              position: 'top-right',
              autoClose: 4000,
            })
            cancel(true)
          } else if (response?.payload?.code === 4400) {
            toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.NAME_ALREADY_EXIST' }), {
              position: 'top-right',
              autoClose: 4000,
            })
          } else {
            toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_THEME' }), {
              position: 'top-right',
              autoClose: 4000,
            })
          }
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  useLayoutEffect(() => {
    translationsSnapshotRef.current = formik.values.translations
    // Instantané avant useEffect : fusion langues avec la dernière saisie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.translations])

  /** Rechargement thème (création / édition) uniquement — pas quand la liste des langues arrive. */
  useEffect(() => {
    if (isThemeLoading) return
    void formik.resetForm({ values: mergeThemeForForm(Theme, langCodes) })
    /** Après reset, pas de deuxième synchro langue avec un snapshot périmé. */
    lastAppliedLangJoinRef.current = langCodes.join(',')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverThemeSig])

  /** Quand /languages se remplit : ajouter les lignes sans effacer la saisie en cours. */
  useEffect(() => {
    if (isThemeLoading) return
    const join = langCodes.join(',')
    if (!join) return
    if (lastAppliedLangJoinRef.current === join) return
    lastAppliedLangJoinRef.current = join
    const prev = translationsSnapshotRef.current
    const next = mergeTranslationsPreserveInput(Theme, langCodes, prev)
    void formik.setFieldValue('translations', next, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langCodes.join(','), isThemeLoading])

  const selectedTranslation = formik.values.translations.find(
    (t) => normLang(t.language) === normLang(selectedLanguage),
  )

  const translationIndex =
    selectedTranslation !== undefined
      ? formik.values.translations.findIndex(
          (t) => normLang(t.language) === normLang(selectedLanguage),
        )
      : -1

  const setNameForLanguage = (name: string) => {
    const langs = [...formik.values.translations]
    let idx = langs.findIndex((t) => normLang(t.language) === normLang(selectedLanguage))
    if (idx === -1) {
      langs.push({ language: selectedLanguage, name })
      idx = langs.length - 1
      void formik.setFieldValue('translations', langs)
      return
    }
    langs[idx] = { ...langs[idx], name }
    void formik.setFieldValue('translations', langs)
    void formik.setFieldTouched(`translations[${idx}].name`, true)
  }

  return (
    <>
      <form id='kt_modal_add_theme_form' className='form' onSubmit={formik.handleSubmit} noValidate>
        <div className='d-flex flex-column gap-5'>
          <div className='fv-row mb-4'>
            <label className='required fw-bold fs-6 mb-2' htmlFor='kt_modal_theme_language'>
              {intl.formatMessage({ id: 'COLUMNS.LANGUAGE' })}
            </label>
            <select
              id='kt_modal_theme_language'
              name='themeLanguage'
              onChange={(e) => setSelectedLanguage(e.target.value)}
              value={selectedLanguage}
              className='form-select form-select-solid'
              disabled={formik.isSubmitting || isThemeLoading}
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>

          <div className='fv-row mb-4'>
            <label className='required fw-bold fs-6 mb-2' htmlFor='kt_modal_theme_name'>
              {intl.formatMessage({ id: 'COLUMNS.NAME' })}
            </label>

            <input
              id='kt_modal_theme_name'
              placeholder={intl.formatMessage({ id: 'COLUMNS.NAME' })}
              value={selectedTranslation?.name ?? ''}
              onChange={(e) => setNameForLanguage(e.target.value)}
              type='text'
              name='themeName'
              className={clsx(
                'form-control form-control-solid',
                translationIndex >= 0 &&
                  formik.touched.translations?.[translationIndex]?.name &&
                  (formik.errors.translations?.[translationIndex] as { name?: string } | undefined)?.name
                  ? 'is-invalid'
                  : '',
              )}
              autoComplete='off'
              disabled={formik.isSubmitting || isThemeLoading}
            />
          </div>
        </div>

        <div className='d-flex justify-content-end gap-3 pt-4'>
          <button
            type='button'
            onClick={() => cancel()}
            className='btn btn-light-primary fw-semibold px-6'
            data-kt-themes-modal-action='cancel'
            disabled={formik.isSubmitting || isThemeLoading}
          >
            {intl.formatMessage({ id: 'HEADER.RESET' })}
          </button>

          <button
            type='submit'
            className='btn btn-primary fw-semibold px-8'
            data-kt-users-modal-action='submit'
            disabled={isThemeLoading || formik.isSubmitting}
          >
            <span className='indicator-label'> {intl.formatMessage({ id: 'HEADER.SAVE' })}</span>
            {(formik.isSubmitting || isThemeLoading) && (
              <span className='indicator-progress'>
                {intl.formatMessage({ id: 'COMMON.PLEASE_WAIT' })}{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
      {isThemeLoading && <DataLoading />}
    </>
  )
}

export { ThemeEditModalForm }
